import React, { createContext, useCallback, useContext, useRef, useState } from "react";

const ConfirmContext = createContext(null);

/**
 * Provider for the global confirm dialog.
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "¿Eliminar?", message: "..." })) { ... }
 *
 * Works on iOS Safari (where window.confirm() is sometimes blocked / silently
 * dismissed inside async handlers).
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, title: "", message: "", confirmLabel: "Eliminar", cancelLabel: "Cancelar", danger: true });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: opts.title || "¿Estás seguro?",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Eliminar",
        cancelLabel: opts.cancelLabel || "Cancelar",
        danger: opts.danger !== false,
      });
    });
  }, []);

  const close = (value) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state.open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4 animate-fade-in-up"
          onClick={() => close(false)}
          data-testid="confirm-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-xl w-full max-w-sm p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${state.danger ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"}`}>
                <i className={`fa-solid ${state.danger ? "fa-triangle-exclamation" : "fa-circle-question"} text-xl`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-extrabold text-lg text-slate-900" data-testid="confirm-title">{state.title}</h3>
                {state.message && <p className="text-slate-600 mt-1 text-sm" data-testid="confirm-message">{state.message}</p>}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => close(false)}
                data-testid="confirm-cancel-btn"
                className="px-5 py-2.5 rounded-full font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                {state.cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                data-testid="confirm-ok-btn"
                className={`px-5 py-2.5 rounded-full text-white font-semibold ${state.danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
                autoFocus
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback for the rare case where the provider is missing
    return async (opts = {}) =>
      typeof window !== "undefined" && window.confirm(opts.message || opts.title || "¿Continuar?");
  }
  return ctx;
}
